import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle, Clock, Loader2, Award, RotateCcw, FileQuestion, Download, Mail, Paperclip, ListTree, ChevronUp, ChevronDown, Printer, Presentation, FileText } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const videoEmbedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const loom = url.match(/loom\.com\/share\/([\w-]+)/);
  if (loom) return `https://www.loom.com/embed/${loom[1]}`;
  return null;
};

const shareInputCls = "w-full rounded-md border border-slate-200 bg-[#EDF4FB] px-3 py-2 text-sm text-slate-900 placeholder:text-muted-foreground/50 focus:outline-none focus:border-cadmium/50 transition-colors";

const CertificateShare = ({ assignmentId }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const res = await api.post("/training/certificate/share", { assignment_id: assignmentId, to_email: email, note });
      toast.success(`Certificate shared`, { description: `Verification email sent to ${res.data.sent_to}` });
      setEmail("");
      setNote("");
      setOpen(false);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="cert-share">
      <button
        data-testid="cert-share-toggle"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 rounded-md border border-moss/60 px-4 py-2 text-xs font-mono uppercase tracking-widest text-green-700 hover:bg-moss/10 transition-colors"
      >
        <Mail size={13} /> Share by Email
      </button>
      {open && (
        <div className="mt-3 space-y-2.5 max-w-md">
          <input data-testid="cert-share-email" type="email" className={shareInputCls} placeholder="landlord or auditor email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="cert-share-note" className={shareInputCls} placeholder="Optional note (max 280 chars)" maxLength={280} value={note} onChange={(e) => setNote(e.target.value)} />
          <button data-testid="cert-share-send" onClick={send} disabled={sending || !email.trim()} className="inline-flex items-center gap-2 rounded-md bg-moss px-4 py-2 text-xs font-mono uppercase tracking-widest text-white hover:opacity-90 transition-opacity disabled:opacity-60">
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Send Verification Email
          </button>
        </div>
      )}
    </div>
  );
};

const FileCard = ({ path, name, icon: Icon, label, testid }) => {
  const [open, setOpen] = useState(true);
  const url = `${BACKEND}/api/files/${path}`;
  const isPdf = (name || path).toLowerCase().endsWith(".pdf");

  if (!isPdf) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" data-testid={testid} className="mb-6 flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 hover:border-cadmium/40 transition-colors">
        <Icon size={15} className="text-sky-600 shrink-0" />
        <span className="text-sm text-slate-900 flex-1 truncate">{label} — {name || "file"}</span>
        <Download size={14} className="text-muted-foreground shrink-0" />
      </a>
    );
  }

  return (
    <div className="mb-6 rounded-md border border-slate-200 bg-slate-50 overflow-hidden" data-testid={testid}>
      <div className="flex items-center gap-3 px-4 py-2.5">
        <Icon size={15} className="text-sky-600 shrink-0" />
        <span className="text-sm text-slate-900 flex-1 truncate">{label} — {name || "document"}</span>
        <button onClick={() => setOpen(!open)} data-testid={`${testid}-toggle`} className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-sky-600 transition-colors">
          {open ? "Hide" : "View"}
        </button>
        <a href={url} target="_blank" rel="noopener noreferrer" aria-label="Download" data-testid={`${testid}-download`} className="text-muted-foreground hover:text-sky-600 transition-colors">
          <Download size={14} />
        </a>
      </div>
      {open && <iframe src={url} title={name || label} className="w-full h-[560px] bg-white border-t border-slate-200" />}
    </div>
  );
};

const ExamReview = ({ review }) => (
  <div className="mt-6 space-y-3 text-left" data-testid="exam-review">
    <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Answer review — correct answers highlighted</p>
    {review.map((r, i) => (
      <div key={i} className={`rounded-md border px-4 py-3 ${r.is_correct ? "border-moss/40 bg-moss/5" : "border-red-300 bg-red-50"}`} data-testid={`review-q-${i}`}>
        <p className="text-sm font-medium text-slate-900 mb-2">
          <span className={`mr-2 font-mono text-[10px] uppercase tracking-wider ${r.is_correct ? "text-green-700" : "text-red-600"}`}>{r.is_correct ? "Correct" : "Missed"}</span>
          {i + 1}. {r.q}
        </p>
        <div className="space-y-1">
          {r.options.map((o, oi) => (
            <p key={oi} className={`rounded-sm px-2 py-1 text-xs ${oi === r.correct ? "bg-moss/15 font-semibold text-green-700" : oi === r.chosen ? "bg-red-100 text-red-600" : "text-slate-500"}`}>
              {String.fromCharCode(65 + oi)}. {o}
              {oi === r.correct ? "  · correct answer" : (oi === r.chosen ? "  · your answer" : "")}
            </p>
          ))}
        </div>
        {r.explanation && (
          <p className="mt-2 border-t border-slate-200 pt-2 text-xs italic text-slate-600" data-testid={`review-explanation-${i}`}>
            Why: {r.explanation}
          </p>
        )}
      </div>
    ))}
  </div>
);

export default function CoursePlayer() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [done, setDone] = useState(new Set());
  const [idx, setIdx] = useState(0);
  const [examMode, setExamMode] = useState(false);
  const [outlineOpen, setOutlineOpen] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/training/assignments/${assignmentId}`);
      setData(res.data);
      setDone(new Set(res.data.assignment.completed_lessons || []));
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
      else { toast.error("Could not load course"); navigate("/training"); }
    }
  }, [assignmentId, navigate]);

  useEffect(() => {
    const link = document.querySelector('link[rel="icon"]');
    const prev = link?.getAttribute("href");
    link?.setAttribute("href", "/hr-logo-mark.png");
    document.title = "OPERA360 HR — Course";
    return () => {
      if (prev) link?.setAttribute("href", prev);
      document.title = "OPERA360 — Operations & Training OS for Community Portfolios";
    };
  }, []);

  useEffect(() => { load(); }, [load]);

  const lessons = useMemo(() => {
    if (!data) return [];
    return data.course.modules.flatMap((m) =>
      m.lessons.map((l) => ({
        ...l,
        moduleTitle: m.title,
        moduleVideo: m.video_url || "",
        moduleSlides: m.slides_path ? { path: m.slides_path, name: m.slides_name } : null,
      }))
    );
  }, [data]);

  if (!data) {
    return (
      <div className="min-h-screen workspace-light bg-[#EDF4FB] flex items-center justify-center" data-testid="course-loading">
        <Loader2 size={24} className="animate-spin text-sky-600" />
      </div>
    );
  }

  const { assignment, course } = data;
  const total = lessons.length;
  const allDone = total > 0 && done.size >= total;
  const overdue = assignment.due_date && assignment.status !== "completed" && assignment.due_date < new Date().toISOString().slice(0, 10);
  const exam = course.exam || [];
  const lesson = lessons[idx];

  const markComplete = async () => {
    setBusy(true);
    try {
      await api.post("/training/progress", { assignment_id: assignmentId, lesson_id: lesson.id });
      const next = new Set(done);
      next.add(lesson.id);
      setDone(next);
      if (idx < total - 1) setIdx(idx + 1);
      else if (exam.length) setExamMode(true);
      else toast.success("Course complete");
    } catch {
      toast.error("Could not save progress");
    } finally {
      setBusy(false);
    }
  };

  const startExam = () => {
    setAnswers(new Array(exam.length).fill(-1));
    setResult(null);
    setExamMode(true);
  };

  const submitExam = async () => {
    if (answers.some((a) => a < 0)) {
      toast.error("Answer every question before submitting");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/training/exam/submit", { assignment_id: assignmentId, answers });
      setResult(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  const progressPct = total ? Math.round((done.size / total) * 100) : 0;

  return (
    <div className="min-h-screen workspace-light bg-[#EDF4FB]" data-testid="course-player">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-[#EDF4FB]/90 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/" aria-label="OPERA360 home"><LogoMark size={26} /></Link>
            <div className="min-w-0">
              <p className="font-cabinet font-bold text-sm text-slate-900 truncate">{course.title}</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{course.category}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <span className="hidden sm:block text-[11px] font-mono text-muted-foreground">{done.size}/{total} lessons</span>
            {assignment.due_date && (
              <span data-testid="player-due" className={`hidden sm:block text-[11px] font-mono ${overdue ? "text-amber-700" : "text-muted-foreground"}`}>
                Due {assignment.due_date}{overdue ? " · overdue" : ""}
              </span>
            )}
            <Link to="/training" data-testid="player-back" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-sky-600 transition-colors">
              <ArrowLeft size={13} /> Hub
            </Link>
          </div>
        </div>
        <div className="h-0.5 bg-slate-100">
          <div className={`h-full ${assignment.passed ? "bg-moss" : "bg-cadmium"} transition-all`} style={{ width: `${progressPct}%` }} />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 grid lg:grid-cols-[280px_1fr] gap-4 lg:gap-6">
        <button
          data-testid="outline-toggle"
          onClick={() => setOutlineOpen(!outlineOpen)}
          className="lg:hidden technical-card w-full px-4 py-3 flex items-center justify-between"
        >
          <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">
            <ListTree size={14} className="text-sky-600" /> Course outline · {done.size}/{total} done
          </span>
          {outlineOpen ? <ChevronUp size={15} className="text-muted-foreground" /> : <ChevronDown size={15} className="text-muted-foreground" />}
        </button>

        <aside className={`technical-card p-4 h-fit lg:sticky lg:top-24 lg:block ${outlineOpen ? "" : "hidden"}`} data-testid="course-outline">
          {course.modules.map((m) => (
            <div key={m.id} className="mb-4 last:mb-0">
              <p className="px-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">{m.title}</p>
              {m.lessons.map((l) => {
                const flatIdx = lessons.findIndex((x) => x.id === l.id);
                const isDone = done.has(l.id);
                const isCurrent = !examMode && flatIdx === idx;
                return (
                  <button
                    key={l.id}
                    data-testid={`outline-lesson-${l.id}`}
                    onClick={() => { setExamMode(false); setIdx(flatIdx); setOutlineOpen(false); }}
                    className={`w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs transition-colors ${isCurrent ? "bg-cadmium/10 text-sky-600" : "text-muted-foreground hover:text-slate-900"}`}
                  >
                    {isDone ? <CheckCircle2 size={13} className="text-green-700 shrink-0" /> : <Circle size={13} className="shrink-0 opacity-40" />}
                    <span className="truncate">{l.title}</span>
                  </button>
                );
              })}
            </div>
          ))}
          {exam.length > 0 && (
            <button
              data-testid="outline-exam"
              onClick={() => { startExam(); setOutlineOpen(false); }}
              disabled={!allDone}
              className={`mt-2 w-full flex items-center gap-2 rounded-sm px-2 py-2 text-left text-xs font-mono uppercase tracking-wider border transition-colors ${allDone ? "border-cadmium/40 text-sky-600 hover:bg-cadmium/10" : "border-slate-200 text-muted-foreground/50 cursor-not-allowed"}`}
            >
              <FileQuestion size={13} /> Final Exam {assignment.passed ? "· passed" : allDone ? "" : "· locked"}
            </button>
          )}
        </aside>

        <div>
          {examMode ? (
            <div className="technical-card p-6 sm:p-8" data-testid="exam-panel">
              <p className="eyebrow mb-3 flex items-center gap-3"><span className="inline-block h-px w-10 bg-cadmium" /> Final Exam</p>
              <h2 className="font-cabinet text-xl sm:text-2xl font-extrabold text-slate-900 mb-1">{course.title}</h2>
              <p className="text-xs text-muted-foreground mb-6">{exam.length} questions · pass mark {course.pass_mark}% · {assignment.attempts ? `${assignment.attempts} attempt(s) so far · best ${assignment.best_score}%` : "first attempt"}</p>

              {result && (
                <div data-testid="exam-result" className={`mb-6 rounded-md border p-5 ${result.passed ? "border-moss/40 bg-moss/10" : "border-amber/40 bg-amber/10"}`}>
                  <p className={`font-mono text-3xl font-semibold ${result.passed ? "text-green-700" : "text-amber-700"}`}>{result.score}%</p>
                  <p className="mt-1 text-sm text-slate-900">
                    {result.passed ? "Passed — course complete." : `Below the ${result.pass_mark}% pass mark — review the lessons and retake.`}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">{result.correct} of {result.total} correct · best score {result.best_score}%</p>
                  {result.next_course && (
                    <div className="mt-3 rounded-md border border-sky-200 bg-sky-50 px-3 py-2.5" data-testid="next-course-unlocked">
                      <p className="text-xs font-mono uppercase tracking-widest text-sky-700">Learning path · next up</p>
                      <p className="mt-0.5 text-sm font-medium text-slate-900">{result.next_course}</p>
                      <p className="text-[11px] text-sky-700/80">Just added to your hub — check your email for the link.</p>
                    </div>
                  )}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {result.passed && (
                      <a href={`${BACKEND}/api/training/certificate/${assignmentId}.pdf`} target="_blank" rel="noreferrer" data-testid="certificate-download" className="inline-flex items-center gap-2 rounded-md bg-moss px-4 py-2 text-xs font-mono uppercase tracking-widest text-white hover:opacity-90 transition-opacity">
                        <Download size={13} /> Download Certificate
                      </a>
                    )}
                    {result.passed && (
                      <a href={`${BACKEND}/api/training/certificate/${assignmentId}.pdf?style=print`} target="_blank" rel="noreferrer" data-testid="certificate-print" className="inline-flex items-center gap-2 rounded-md border border-moss/60 px-4 py-2 text-xs font-mono uppercase tracking-widest text-green-700 hover:bg-moss/10 transition-colors">
                        <Printer size={13} /> Print Version
                      </a>
                    )}
                    {!result.passed && (
                      <button data-testid="exam-retake" onClick={startExam} className="inline-flex items-center gap-2 rounded-md bg-cadmium px-4 py-2 text-xs font-mono uppercase tracking-widest text-white hover:bg-cadmium-hover transition-colors">
                        <RotateCcw size={13} /> Retake Exam
                      </button>
                    )}
                    <a href={`${BACKEND}/api/training/exam-sheet/${assignmentId}.pdf`} target="_blank" rel="noreferrer" data-testid="exam-sheet-download" className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-slate-900 transition-colors">
                      <FileText size={13} /> Scored Exam Sheet
                    </a>
                    <Link to="/training" data-testid="exam-back-hub" className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-slate-900 transition-colors">
                      Back to Hub
                    </Link>
                  </div>
                  {result.review && <ExamReview review={result.review} />}
                  {result.passed && (
                    <div className="mt-4">
                      <CertificateShare assignmentId={assignmentId} />
                    </div>
                  )}
                </div>
              )}

              {!result && data?.review && (
                <div className="mb-6">
                  <button data-testid="review-toggle" onClick={() => setReviewOpen(!reviewOpen)} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-slate-900 transition-colors">
                    <ChevronDown size={13} className={reviewOpen ? "rotate-180 transition-transform" : "transition-transform"} /> Review last attempt
                  </button>
                  {reviewOpen && <ExamReview review={data.review} />}
                </div>
              )}

              {!result && (
                <>
                  {exam.map((q, qi) => (
                    <div key={qi} className="mb-6" data-testid={`exam-q-${qi}`}>
                      <p className="text-sm font-semibold text-slate-900 mb-3">{qi + 1}. {q.q}</p>
                      <div className="space-y-2">
                        {q.options.map((opt, oi) => (
                          <label key={oi} className={`flex items-center gap-3 rounded-md border px-3 py-2.5 text-sm cursor-pointer transition-colors ${answers[qi] === oi ? "border-cadmium/60 bg-cadmium/10 text-slate-900" : "border-slate-200 text-muted-foreground hover:border-white/25"}`}>
                            <input
                              type="radio"
                              name={`q-${qi}`}
                              data-testid={`exam-q${qi}-opt${oi}`}
                              checked={answers[qi] === oi}
                              onChange={() => { const next = [...answers]; next[qi] = oi; setAnswers(next); }}
                              className="accent-[#2E9CFF]"
                            />
                            {opt}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  <button
                    data-testid="exam-submit"
                    onClick={submitExam}
                    disabled={busy}
                    className="inline-flex items-center gap-2 rounded-md bg-cadmium px-6 py-3 text-xs font-mono uppercase tracking-widest text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60"
                  >
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Award size={14} />}
                    Submit Exam
                  </button>
                </>
              )}
            </div>
          ) : lesson ? (
            <div className="technical-card p-6 sm:p-8" data-testid="lesson-panel">
              <p className="text-[10px] font-mono uppercase tracking-widest text-sky-600 mb-2">{lesson.moduleTitle}</p>
              <h2 className="font-cabinet text-xl sm:text-2xl font-extrabold text-slate-900 mb-2">{lesson.title}</h2>
              <p className="text-[11px] font-mono text-muted-foreground flex items-center gap-1.5 mb-6">
                <Clock size={12} /> {lesson.duration_min} min · Lesson {idx + 1} of {total}
              </p>
              {lesson.moduleVideo && (videoEmbedUrl(lesson.moduleVideo) ? (
                <div className="mb-4 aspect-video rounded-md overflow-hidden border border-slate-200" data-testid="module-video">
                  <iframe
                    src={videoEmbedUrl(lesson.moduleVideo)}
                    title={`${lesson.moduleTitle} video`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <a href={lesson.moduleVideo} target="_blank" rel="noopener noreferrer" data-testid="module-video" className="mb-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-sky-600 hover:text-cadmium-hover transition-colors">
                  Watch module video ↗
                </a>
              ))}
              {lesson.moduleSlides && (
                <FileCard path={lesson.moduleSlides.path} name={lesson.moduleSlides.name} icon={Presentation} label="Module slides" testid="module-slides" />
              )}
              {lesson.video_url && (videoEmbedUrl(lesson.video_url) ? (
                <div className="mb-6 aspect-video rounded-md overflow-hidden border border-slate-200" data-testid="lesson-video">
                  <iframe
                    src={videoEmbedUrl(lesson.video_url)}
                    title={lesson.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <a href={lesson.video_url} target="_blank" rel="noopener noreferrer" data-testid="lesson-video" className="mb-6 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-sky-600 hover:text-cadmium-hover transition-colors">
                  Watch lesson video ↗
                </a>
              ))}
              {lesson.attachment_path && (
                <FileCard path={lesson.attachment_path} name={lesson.attachment_name} icon={Paperclip} label="Attachment" testid="lesson-attachment" />
              )}
              <div className="text-sm leading-relaxed text-[#C9CED6] whitespace-pre-wrap" data-testid="lesson-body">{lesson.body}</div>
              <div className="mt-8 flex items-center gap-3">
                {done.has(lesson.id) ? (
                  <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-green-700">
                    <CheckCircle2 size={14} /> Completed
                  </span>
                ) : (
                  <button
                    data-testid="lesson-complete"
                    onClick={markComplete}
                    disabled={busy}
                    className="inline-flex items-center gap-2 rounded-md bg-cadmium px-5 py-2.5 text-xs font-mono uppercase tracking-widest text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60"
                  >
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    Mark Complete & Continue
                  </button>
                )}
                {done.has(lesson.id) && idx < total - 1 && (
                  <button data-testid="lesson-next" onClick={() => setIdx(idx + 1)} className="text-xs font-mono uppercase tracking-widest text-sky-600 hover:text-white transition-colors">
                    Next lesson →
                  </button>
                )}
              </div>
            </div>
          ) : null}

          {allDone && !examMode && exam.length > 0 && !assignment.passed && (
            <div className="technical-card p-6 mt-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="exam-unlocked">
              <Award size={22} className="text-sky-600 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900">All lessons complete — final exam unlocked.</p>
                <p className="text-xs text-muted-foreground mt-0.5">{exam.length} questions · pass mark {course.pass_mark}%</p>
              </div>
              <button data-testid="exam-start" onClick={startExam} className="inline-flex items-center gap-2 rounded-md bg-cadmium px-5 py-2.5 text-xs font-mono uppercase tracking-widest text-white hover:bg-cadmium-hover transition-colors">
                Start Exam
              </button>
            </div>
          )}

          {assignment.passed && (
            <div className="rounded-md border border-moss/40 bg-moss/10 p-5 mt-6" data-testid="course-passed-banner">
              <p className="text-sm font-semibold text-green-700">Course passed — best score {assignment.best_score}%</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <a href={`${BACKEND}/api/training/certificate/${assignmentId}.pdf`} target="_blank" rel="noreferrer" data-testid="certificate-download-banner" className="inline-flex items-center gap-2 rounded-md bg-moss px-4 py-2 text-xs font-mono uppercase tracking-widest text-white hover:opacity-90 transition-opacity">
                  <Download size={13} /> Download Certificate
                </a>
                <a href={`${BACKEND}/api/training/certificate/${assignmentId}.pdf?style=print`} target="_blank" rel="noreferrer" data-testid="certificate-print-banner" className="inline-flex items-center gap-2 rounded-md border border-moss/60 px-4 py-2 text-xs font-mono uppercase tracking-widest text-green-700 hover:bg-moss/10 transition-colors">
                  <Printer size={13} /> Print Version
                </a>
                <a href={`${BACKEND}/api/training/exam-sheet/${assignmentId}.pdf`} target="_blank" rel="noreferrer" data-testid="exam-sheet-download-banner" className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-slate-900 transition-colors">
                  <FileText size={13} /> Scored Exam Sheet
                </a>
              </div>
              <div className="mt-3">
                <CertificateShare assignmentId={assignmentId} />
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
